import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Input, Modal, DatePicker } from 'antd';
import { SearchOutlined, ReloadOutlined, DownloadOutlined, FileTextOutlined, EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

interface AnnouncementItem {
  id: number;
  project_no: string;
  project_name: string;
  bidding_unit: string;
  open_bid_time: string;
  file_get_end_time: string;
}

interface RegistrationListItem {
  id: number;
  seqNo: number;
  paymentTime: string;
  supplierName: string;
  contactName: string;
  contactPhone: string;
  email: string;
  packageNames: string;
  hasBiddingFile: string;
  unionMember?: string;
}

interface RegistrationListData {
  projectName: string;
  projectNo: string;
  fileGetTimeRange: string;
  list: RegistrationListItem[];
}

export default function SupplierRegistrationList() {
  const [announcements, setAnnouncements] = useState<AnnouncementItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [exportingId, setExportingId] = useState<number | null>(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [registrationData, setRegistrationData] = useState<RegistrationListData | null>(null);
  const [editingPaymentTime, setEditingPaymentTime] = useState<{ id: number; value: dayjs.Dayjs | null } | null>(null);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      if (searchKeyword) {
        params.append('keyword', searchKeyword);
      }
      
      const resp = await fetch(`/api/admin/supplier-registration-list?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setAnnouncements(resp.data || []);
      } else {
        message.error(resp?.error || '获取数据失败');
      }
    } catch (err) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchAnnouncements();
  };

  const handleExport = async (projectNo: string) => {
    try {
      setExportingId(announcements.find(a => a.project_no === projectNo)?.id || null);
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`/api/admin/supplier-registration-list/export?project_no=${encodeURIComponent(projectNo)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `供应商报名清单_${projectNo}_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        message.success('导出成功');
      } else {
        const result = await response.json();
        message.error(result?.error || '导出失败');
      }
    } catch (err) {
      message.error('导出失败');
    } finally {
      setExportingId(null);
    }
  };

  // 格式化时间
  const formatDateTime = (time: string | null) => {
    if (!time) return '-';
    return dayjs(time).format('YYYY-MM-DD HH:mm:ss');
  };

  const isWithin8Hours = (endTime: string): boolean => {
    if (!endTime) return false;
    const now = dayjs();
    const end = dayjs(endTime);
    const diffHours = end.diff(now, 'hour', true);
    return diffHours > 0 && diffHours <= 8;
  };

  // 查看清单
  const handleViewList = async (projectNo: string) => {
    try {
      setViewLoading(true);
      setViewModalVisible(true);
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`/api/admin/supplier-registration-list/data?project_no=${encodeURIComponent(projectNo)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.ok) {
          setRegistrationData(result.data);
        } else {
          message.error(result?.error || '获取数据失败');
          setViewModalVisible(false);
        }
      } else {
        const result = await response.json();
        message.error(result?.error || '获取数据失败');
        setViewModalVisible(false);
      }
    } catch (err) {
      message.error('获取数据失败');
      setViewModalVisible(false);
    } finally {
      setViewLoading(false);
    }
  };

  // 保存购买时间
  const handleSavePaymentTime = async () => {
    if (!editingPaymentTime || !editingPaymentTime.value) {
      message.error('请选择时间');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const paymentTime = editingPaymentTime.value.format('YYYY-MM-DD HH:mm:ss');
      
      console.log('发送保存请求:', { id: editingPaymentTime.id, paymentTime });
      
      const response = await fetch(`/api/admin/trade-orders/${editingPaymentTime.id}/payment-time`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ payment_time: paymentTime }),
      });
      
      console.log('响应状态:', response.status);
      const result = await response.json();
      console.log('响应结果:', result);
      
      if (response.ok) {
        if (result.ok) {
          message.success('更新成功');
          // 更新本地数据
          if (registrationData) {
            const updatedList = registrationData.list.map(item => {
              if (item.id === editingPaymentTime.id) {
                return { ...item, paymentTime: paymentTime };
              }
              return item;
            });
            setRegistrationData({ ...registrationData, list: updatedList });
          }
          setEditingPaymentTime(null);
        } else {
          message.error(result?.error || '更新失败');
        }
      } else {
        message.error(result?.error || '更新失败');
      }
    } catch (err) {
      console.error('保存购买时间错误:', err);
      message.error('更新失败');
    }
  };

  // 清单表格列定义
  const listColumns: ColumnsType<RegistrationListItem> = [
    {
      title: '序号',
      dataIndex: 'seqNo',
      key: 'seqNo',
      width: 80,
      align: 'center',
    },
    {
      title: '购买时间',
      dataIndex: 'paymentTime',
      key: 'paymentTime',
      width: 220,
      render: (text: string, record: RegistrationListItem) => {
        if (editingPaymentTime?.id === record.id) {
          return (
            <Space size="small">
              <DatePicker
                showTime
                format="YYYY-MM-DD HH:mm:ss"
                value={editingPaymentTime.value}
                onChange={(value) => setEditingPaymentTime({ id: record.id, value })}
                style={{ width: '180px' }}
              />
              <Button 
                type="primary" 
                size="small"
                onClick={handleSavePaymentTime}
              >
                保存
              </Button>
              <Button 
                size="small"
                onClick={() => setEditingPaymentTime(null)}
              >
                取消
              </Button>
            </Space>
          );
        }
        return (
          <span
            style={{ cursor: 'pointer', color: '#3b82f6' }}
            onClick={() => setEditingPaymentTime({ id: record.id, value: text ? dayjs(text) : dayjs() })}
          >
            {formatDateTime(text)}
          </span>
        );
      },
    },
    {
      title: '供应商名称',
      dataIndex: 'supplierName',
      key: 'supplierName',
      width: 200,
    },
    {
      title: '联合体',
      dataIndex: 'unionMember',
      key: 'unionMember',
      width: 200,
      render: (text: string) => text ? text : <span style={{ color: '#9ca3af' }}>-</span>,
    },
    {
      title: '联系人',
      dataIndex: 'contactName',
      key: 'contactName',
      width: 120,
    },
    {
      title: '联系方式',
      dataIndex: 'contactPhone',
      key: 'contactPhone',
      width: 140,
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      width: 200,
    },
    {
      title: '购买包号',
      dataIndex: 'packageNames',
      key: 'packageNames',
      width: 200,
    },
    {
      title: '是否已上传投标文件',
      dataIndex: 'hasBiddingFile',
      key: 'hasBiddingFile',
      width: 160,
      align: 'center',
      render: (text: string) => (
        <span style={{ color: text === '已上传' ? '#10b981' : '#ef4444' }}>{text}</span>
      ),
    },
  ];

  const columns: ColumnsType<AnnouncementItem> = [
    {
      title: '项目编号',
      dataIndex: 'project_no',
      key: 'project_no',
      width: 180,
      render: (text) => <span style={{ fontFamily: 'monospace', color: '#1f2937' }}>{text}</span>
    },
    {
      title: '项目名称',
      dataIndex: 'project_name',
      key: 'project_name',
      width: 280,
      ellipsis: true,
      render: (text) => <span style={{ fontWeight: 500, color: '#1f2937' }}>{text}</span>
    },
    {
      title: '采购人',
      dataIndex: 'bidding_unit',
      key: 'bidding_unit',
      width: 200,
      ellipsis: true,
    },
    {
      title: '开标时间',
      dataIndex: 'open_bid_time',
      key: 'open_bid_time',
      width: 180,
      render: (text) => <span style={{ color: '#6b7280', fontSize: 13 }}>{formatDateTime(text)}</span>
    },
    {
      title: '售卖截止日期',
      dataIndex: 'file_get_end_time',
      key: 'file_get_end_time',
      width: 180,
      render: (text: string) => {
        if (!text) return <span style={{ color: '#9ca3af' }}>-</span>;
        const urgent = isWithin8Hours(text);
        return (
          <span style={{
            color: urgent ? '#ff4d4f' : '#6b7280',
            fontWeight: urgent ? 600 : undefined,
            fontSize: 13,
          }}>
            {formatDateTime(text)}
          </span>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 240,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="default"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewList(record.project_no)}
            style={{ borderRadius: 8 }}
          >
            查看清单
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => handleExport(record.project_no)}
            loading={exportingId === record.id}
            style={{ 
              borderRadius: 8,
              background: 'linear-gradient(135deg, #10b981, #059669)',
              border: 'none'
            }}
          >
            导出报名清单
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon">
            <FileTextOutlined />
          </div>
          <div>
            <h2>供应商报名清单</h2>
            <p>查看项目的供应商报名情况并导出清单</p>
          </div>
        </div>
        
        <Space size="middle">
          <Input
            placeholder="搜索项目编号/名称"
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 220, borderRadius: 10 }}
            allowClear
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchAnnouncements}
            style={{ borderRadius: 10 }}
          >
            刷新
          </Button>
        </Space>
      </div>
      
      {/* 表格内容 */}
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#3b82f6', fontWeight: 600 }}>{announcements.length}</span> 条记录
        </div>
        
        <Table
          columns={columns}
          dataSource={announcements}
          loading={loading}
          rowKey="id"
          className="admin-table"
          scroll={{ x: 1080 }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </div>

      {/* 查看清单弹窗 */}
      <Modal
        title={
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            购买标书供应商汇总表
          </div>
        }
        open={viewModalVisible}
        onCancel={() => {
          setViewModalVisible(false);
          setRegistrationData(null);
          setEditingPaymentTime(null);
        }}
        footer={null}
        width={1200}
        destroyOnClose
      >
        {registrationData && (
          <div>
            {/* 表头信息 */}
            <div style={{ marginBottom: 16, padding: '12px 16px', backgroundColor: '#f9fafb', borderRadius: 8 }}>
              <div style={{ marginBottom: 8, fontSize: 14, color: '#374151' }}>
                <strong>项目名称：</strong>{registrationData.projectName}
              </div>
              <div style={{ marginBottom: 8, fontSize: 14, color: '#374151' }}>
                <strong>项目编号：</strong>{registrationData.projectNo}
              </div>
              <div style={{ fontSize: 14, color: '#374151' }}>
                <strong>标书售卖时间：</strong>{registrationData.fileGetTimeRange}
              </div>
            </div>

            {/* 数据表格 */}
            <Table
              columns={listColumns}
              dataSource={registrationData.list}
              loading={viewLoading}
              rowKey="id"
              pagination={{
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              }}
              scroll={{ x: 1000 }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
